import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import { getIncomePeriods } from "../utils/incomePeriods";
import {
  getAllFCIStatements,
  getFCIStatementsPage,
} from "./fciStatementsService";
import {
  fciReportFilters,
  HISTORICAL_FCI_REPORT_ID,
} from "./fciStatementReportScope";
import {
  formatStatementDate,
  statementDateParts,
} from "./statementDate";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import SummaryCards from "../../DesignSystem/SummaryCards/SummaryCards";
import ButtonTextLink from "../../DesignSystem/Buttons/ButtonTextLink";
import {
  commissionCategoryOf,
  COMMISSION_CATEGORIES,
} from "../MonthlyPayments/components/CommissionStatementPDF/utils/commissionBreakdown";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import "../../../css/App.css";

const PAGE_SIZE = 1000;
const VISIBLE_COLUMNS = [
  "Index",
  "PaymentId",
  "Recipient",
  "PaymentCreated",
  "PolicyAdviser",
  "ClientName",
  "PolicyAccountNumber",
  "PolicyProvider",
  "BusinessType",
  "CommissionType",
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
  "IntroducerName",
];

const downloadName = (extension) => {
  const date = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).replace(/\//g, "-");
  return `fci-statements-${date}.${extension}`;
};

const formatPeriodDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB");
};

const formatPeriodStart = (item) => {
  const explicitStart =
    item.startDatetime || item.startDate || item.incomePeriodStartDatetime;
  if (explicitStart) return formatPeriodDate(explicitStart);
  const encodedStart = String(item.incomePeriodId || "").match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );
  if (!encodedStart) {
    return String(item.incomePeriodName || item.incomePeriodId || "");
  }
  const [, year, day, month] = encodedStart;
  return `${day}/${month}/${year}`;
};

const formatColumnName = (column) =>
  (String(column) === "PaymentId"
    ? "Payment Entry"
    : String(column) === "PolicyAdviser"
    ? "Selling Adviser"
    : String(column) === "PolicyAccountNumber"
      ? "Policy Number"
      : String(column) === "IntroducerName" || String(column) === "Introducer"
        ? "Introducers"
        : String(column))
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();

const formatRecipient = (row) => [row.Recipient, row.RecipientType]
  .map((value) => String(value ?? "").trim())
  .filter(Boolean)
  .join(" - ");
const introducerValues = (value) => (Array.isArray(value) ? value : [value])
  .flatMap((item) => String(item ?? "").split(","))
  .map((item) => item.trim())
  .filter(Boolean);
const formatIntroducers = (value) => introducerValues(value).join(",\n");
const IntroducerList = ({ value }) => {
  const items = introducerValues(value);
  return (
    <span className="fit-column">
      {items.map((item, index) => (
        <span key={`${item}-${index}`}>{item}{index < items.length - 1 ? "," : ""}</span>
      ))}
    </span>
  );
};
const money = (value) => Number(value || 0).toLocaleString("en-GB", {
  style: "currency",
  currency: "GBP",
});
const isNumericColumn = (column) => [
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
].includes(column);

const commissionAmount = (row) => {
  const amount = Number(String(row?.PaymentAmount ?? 0).replace(/[,£]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const buildLiveCommissionBreakdown = (rows = []) => {
  const categories = Object.fromEntries([
    ...COMMISSION_CATEGORIES.map(({ id }) => [id, { count: 0, amount: 0 }]),
    ["uncategorised", { count: 0, amount: 0 }],
  ]);
  rows.forEach((row) => {
    const category = commissionCategoryOf(row) || "uncategorised";
    categories[category].count += 1;
    categories[category].amount += commissionAmount(row);
  });
  return {
    categories,
    total: rows.reduce((sum, row) => sum + commissionAmount(row), 0),
  };
};

const columnValue = (row, column) => {
  if (column === "NormalisedSplit") return row.NormalisedSplit ?? row.Split;
  if (column === "IntroducerName") return formatIntroducers(row.IntroducerName ?? row.Introducers);
  return row[column];
};

const StatementDate = ({ value }) => {
  const parts = statementDateParts(value);
  return (
    <span className="fit-column" style={{ whiteSpace: "nowrap", minWidth: "max-content" }}>
      <span>{parts.date}</span>
      {parts.time ? <span>{parts.time}</span> : null}
    </span>
  );
};

const LiveFCIStatementsPage = () => {
  const { userEmail } = useUserEmail();
  const [periodOptions, setPeriodOptions] = useState([]);
  const [result, setResult] = useState({
    rows: [],
    total: 0,
    totals: { paymentAmount: 0, grossReceived: 0 },
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const filters = useMemo(() => ({}), []);
  const [sort, setSort] = useState({ column: "PaymentCreated", direction: "desc" });
  const [period, setPeriod] = useState("");
  useEffect(() => {
    getIncomePeriods()
      .then((items) => {
        const options = items.map((item) => ({
          id: String(item.incomePeriodId),
          name: `${formatPeriodStart(item)} - ${formatPeriodDate(item.endDatetime) || "not closed"}`,
        }));
        if (items[0]) {
          options.unshift({
            id: "current",
            name: `${formatPeriodDate(items[0].endDatetime) || "Latest close"} - Current`,
          });
        }
        // options.splice(items[0] ? 1 : 0, 0, {
        //   id: HISTORICAL_FCI_REPORT_ID,
        //   name: "January to September 2026",
        // });
        setPeriodOptions(options);
        setPeriod((selected) => options.some((option) => option.id === selected) ? selected : (options[0]?.id || ""));
      })
      .catch((loadError) => setError(loadError.message));
  }, []);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [breakdownCollapsed, setBreakdownCollapsed] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");
  const [commissionBreakdown, setCommissionBreakdown] = useState(() =>
    buildLiveCommissionBreakdown([]),
  );
  const requestIdRef = useRef(0);
  const breakdownRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    if (!period) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError("");
    setResult((current) => ({ ...current, rows: [], total: 0, page }));
    try {
      const nextResult = await getFCIStatementsPage(page, PAGE_SIZE, userEmail, {
        ...filters,
        ...fciReportFilters(period),
        _search: debouncedSearch,
        _sort: sort.column,
        _direction: sort.direction,
      });
      if (requestId !== requestIdRef.current) return;
      setResult(nextResult);
      setLastUpdated(new Date());
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      setError(loadError.message);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [page, userEmail, filters, debouncedSearch, sort, period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (breakdownCollapsed || !period) return undefined;
    const requestId = breakdownRequestIdRef.current + 1;
    breakdownRequestIdRef.current = requestId;
    setBreakdownLoading(true);
    setBreakdownError("");
    getAllFCIStatements(userEmail, {
      ...filters,
      ...fciReportFilters(period),
      _search: debouncedSearch,
      _sort: sort.column,
      _direction: sort.direction,
      _export: "true",
    })
      .then((breakdownResult) => {
        if (requestId !== breakdownRequestIdRef.current) return;
        setCommissionBreakdown(buildLiveCommissionBreakdown(breakdownResult.rows || []));
      })
      .catch((breakdownLoadError) => {
        if (requestId !== breakdownRequestIdRef.current) return;
        setCommissionBreakdown(buildLiveCommissionBreakdown([]));
        setBreakdownError(
          breakdownLoadError.message || "Unable to load the commission breakdown.",
        );
      })
      .finally(() => {
        if (requestId === breakdownRequestIdRef.current) setBreakdownLoading(false);
      });
    return () => {
      breakdownRequestIdRef.current += 1;
    };
  }, [breakdownCollapsed, debouncedSearch, filters, period, sort, userEmail]);

  const columns = VISIBLE_COLUMNS;
  const visibleRows = result.rows;
  const totalPages = Math.max(
    1,
    Math.ceil(result.total / (result.pageSize || PAGE_SIZE)),
  );
  const summaryCards = useMemo(
    () => [
      { id: "payment-amount", label: "Total payment amount", value: Number(result.totals?.paymentAmount || 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" }), tone: "teal" },
      { id: "total", label: "Total entries", value: result.total.toLocaleString(), tone: "blue" },
      { id: "page", label: "Current page", value: `${page} of ${totalPages}`, tone: "cream" },
      // { id: "visible", label: "Entries shown", value: visibleRows.length.toLocaleString(), tone: "green" },
    ],
    [page, result.total, result.totals, totalPages, visibleRows.length],
  );
  const breakdownCards = useMemo(() => {
    const tones = ["blue", "green", "purple", "teal", "pink", "cream"];
    const categories = COMMISSION_CATEGORIES.map(({ id, label }, index) => {
      const category = commissionBreakdown.categories?.[id] || {};
      return {
        id,
        label: `${label}${category.count ? ` (${category.count})` : ""}`,
        value: money(category.amount),
        tone: tones[index % tones.length],
      };
    });
    const uncategorised = commissionBreakdown.categories?.uncategorised || {};
    if (uncategorised.count || uncategorised.amount) {
      categories.push({
        id: "uncategorised",
        label: `Uncategorised (${uncategorised.count || 0})`,
        value: money(uncategorised.amount),
        tone: tones[categories.length % tones.length],
      });
    }
    return [
      ...categories,
      // {
      //   id: "total-commission",
      //   label: "Total Comm.",
      //   value: money(commissionBreakdown.total),
      //   tone: "blue",
      // },
    ];
  }, [commissionBreakdown]);
  const exportAll = async (format) => {
    setExporting(true);
    setError("");
    try {
      const exportFilters = {
        ...filters,
        ...fciReportFilters(period),
        _search: search,
        _sort: sort.column,
        _direction: sort.direction,
        _export: "true",
      };
      const exportResult = await getAllFCIStatements(userEmail, exportFilters);
      const allRows = [...exportResult.rows];

      const exportRows = allRows.map((row) => {
        const copy = {};
        columns.forEach((column) => {
          if (column === "Index") return;
          const heading = column === "PaymentId"
            ? "Payment Entry"
            : column === "PolicyAdviser"
            ? "Selling Adviser"
            : column === "ClientName"
              ? "Client Name"
            : column === "PolicyAccountNumber"
              ? "Policy Number"
              : column === "NormalisedSplit"
            ? "Split"
            : column === "IntroducerName" || column === "Introducer"
              ? "Introducers"
              : column;
          copy[heading] = column === "Recipient"
            ? formatRecipient(row)
            : column === "PaymentCreated"
              ? formatStatementDate(row[column])
              : columnValue(row, column);
        });
        return copy;
      });
      const sheet = XLSX.utils.json_to_sheet(exportRows);
      const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
      sheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
      sheet["!cols"] = Object.keys(exportRows[0] || {}).map(() => ({ wch: 18 }));
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const header = sheet[XLSX.utils.encode_cell({ r: 0, c: column })];
        if (header) header.s = { fill: { fgColor: { rgb: "081E56" } }, font: { color: { rgb: "FFFFFF" }, bold: true } };
        for (let row = 1; row <= range.e.r; row += 1) { const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })]; if (cell) cell.s = { fill: { fgColor: { rgb: row % 2 ? "EAF4FB" : "FFFFFF" } }, border: { bottom: { style: "thin", color: { rgb: "D6E0EA" } } } }; }
      }
      const exportColumns = Object.keys(exportRows[0] || {});
      const introducersColumn = exportColumns.indexOf("Introducers");
      if (introducersColumn >= 0) {
        allRows.forEach((_, index) => {
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: introducersColumn })];
          if (cell) cell.s = {
            ...cell.s,
            alignment: { vertical: "top", wrapText: true },
          };
        });
      }
      const clientColumn = exportColumns.indexOf("Client Name");
      if (clientColumn >= 0) {
        allRows.forEach((row, index) => {
          if (!row.ClientUrl) return;
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: clientColumn })];
          if (!cell) return;
          cell.l = { Target: row.ClientUrl, Tooltip: "Open client in Intelliflo" };
          cell.s = {
            ...cell.s,
            font: { color: { rgb: "0563C1" }, underline: true },
          };
        });
      }
      const policyColumn = exportColumns.indexOf("Policy Number");
      if (policyColumn >= 0) {
        allRows.forEach((row, index) => {
          if (!row.PlanUrl) return;
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: policyColumn })];
          if (!cell) return;
          cell.l = { Target: row.PlanUrl, Tooltip: "Open plan in Intelliflo" };
          cell.s = {
            ...cell.s,
            font: { color: { rgb: "0563C1" }, underline: true },
          };
        });
      }
      if (format === "csv") {
        saveAs(
          new Blob([XLSX.utils.sheet_to_csv(sheet)], {
            type: "text/csv;charset=utf-8",
          }),
          downloadName("csv"),
        );
        return;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "FCI Statements");
      saveAs(
        new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })]),
        downloadName("xlsx"),
      );
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Live FCI Statements</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />

      <main className="Main-Container table-contained-page column gap-m">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            { label: "Live FCI Statements" },
          ]}
        />

        <header className="row space-between gap-l">
          <div className="fit-column gap-xs">
            <h3 className="FP_H3">Live FCI Statements</h3>
            <p className="FP_Help_Text primary">
              View and export live commission data from Snowflake.
            </p>
            {lastUpdated && (
              <p className="FP_Help_Text primary">
                Last updated: {lastUpdated.toLocaleString("en-GB")}
              </p>
            )}
          </div>

          <div className="row gap-s justify-flex-end">
            <div className="fci-period-control">
              <CustomSelect
                label="Income period"
                options={periodOptions}
                value={period}
                overlay={loading}
                onChange={(event) => {
                  setPage(1);
                  setPeriod(event.target.value);
                }}
              />
            </div>
            <div className="fci-search-control">
              <SearchField
                id="live-fci-search"
                value={search}
                label="Search"
                onChange={(event) => {
                  setPage(1);
                  setSearch(event.target.value);
                }}
                placeholder="Search all statements"
                tooltip="Searches every current-period statement, including payment entry, recipient, CRM ID, payment date, policy adviser, client, account number, provider, business type, commission type, amounts, split, and introducers. Use multiple words to narrow the results."
              />
            </div>
            {/* <SecondaryButton
              label={loading ? "Refreshing..." : "Refresh"}
              onClick={load}
              disabled={loading}
            />
            <SecondaryButton
              label="Export CSV"
              onClick={() => exportAll("csv")}
              disabled={exporting}
            /> */}
          </div>
        </header>
        <div className="row space-between gap-l">
          <PrimaryButton
            type="button"
            label={exporting ? "EXPORTING..." : "EXPORT EXCEL"}
            onClick={() => exportAll("xlsx")}
            disabled={exporting}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>

        <section className="bg-white card-corner-radius p-m fit-column gap-s">
          <SummaryCards cards={summaryCards} grid="3" loading={loading} />

          <div className="row justify-flex-end">
            <ButtonTextLink
              label={breakdownCollapsed ? "View Breakdown" : "Hide Breakdown"}
              onClick={() => setBreakdownCollapsed((collapsed) => !collapsed)}
              aria-expanded={!breakdownCollapsed}
              aria-controls="live-fci-commission-breakdown"
            />
          </div>
          {!breakdownCollapsed ? (
            <section
              id="live-fci-commission-breakdown"
              className="fit-column gap-s"
              aria-label="Commission breakdown"
            >
              {breakdownError ? (
                <p className="FP_Help_Text text-error" role="alert">{breakdownError}</p>
              ) : null}
              <SummaryCards
                cards={breakdownCards}
                grid="5"
                loading={breakdownLoading}
              />
            </section>
          ) : null}
        </section>

        {/* {error && (
          <AlertBanner
            type="error"
            message={error}
            onClose={() => setError("")}
          />
        )} */}

        <section className="card-corner-radius">
          <div className="table-scroll-container">
            <table>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className={`FP_Help_Text_Semibold uppercase white-text ${isNumericColumn(column) ? "text-align-right" : ""}`}
                    >
                      {column === "NormalisedSplit" ? "Split" : formatColumnName(column)}
                      {/* <button type="button" className="table-sort-button" onClick={() => { setPage(1); setSort((current) => ({ column, direction: current.column === column && current.direction === "asc" ? "desc" : "asc" })); }} aria-label={`Sort by ${formatColumnName(column)}`}>
                        <SortIcon className={`table-sort-icon ${sort.column === column ? "is-active" : ""} ${sort.column === column && sort.direction === "desc" ? "is-desc" : ""}`} aria-hidden="true" />
                      </button> */}
                      {/* <input value={filters[column] || ""} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, [column]: event.target.value })); }} placeholder="Filter" aria-label={`Filter ${formatColumnName(column)}`} /> */}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      className="FP_Help_Text primary"
                    >
                      Loading live FCI data...
                    </td>
                  </tr>
                ) : visibleRows.length ? (
                  visibleRows.map((row, index) => (
                    <tr key={`${row.PaymentId || row.CashMatchId || "row"}-${index}`}>
                      {columns.map((column) => (
                        <td key={column} className={`FP_Help_Text text-black ${isNumericColumn(column) ? "text-align-right" : ""}`}>
                          {column === "ClientName" && row.ClientUrl
                            ? <a className="table-url-link text-primary-1" href={row.ClientUrl} target="_blank" rel="noreferrer">{String(row.ClientName || "Open client")}</a>
                            : column === "PolicyAccountNumber" && row.PlanUrl
                              ? <a className="table-url-link text-primary-1" href={row.PlanUrl} target="_blank" rel="noreferrer">{String(row.PolicyAccountNumber || "Open plan")}</a>
                            : column === "NormalisedSplit" && columnValue(row, column) !== null && columnValue(row, column) !== undefined
                            ? `${Number(columnValue(row, column)).toLocaleString("en-GB", { maximumFractionDigits: 4 })}%`
                            : column === "Recipient"
                              ? formatRecipient(row)
                              : column === "IntroducerName"
                                ? <IntroducerList value={row.IntroducerName ?? row.Introducers} />
                              : column === "PaymentAmount" || column === "GrossReceived"
                                ? money(columnValue(row, column))
                              : column === "PaymentCreated"
                                ? <StatementDate value={row[column]} />
                                : String(columnValue(row, column) ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      className="FP_Help_Text primary"
                    >
                      No FCI entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LiveFCIStatementsPage;
